import QueryString from "qs";
import { useEffect, useReducer, useState } from "react";
import { Edit } from "react-feather";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button, Card, Col, Row } from "reactstrap";
import { toast } from "react-hot-toast";
import DataGrid from "../../components/dataGrid/DataGrid";
import FormInput from "../../components/FormInput";
import { EmployeeData, getEmployees } from "../../services/employees.service";

const initialState = {
  results: [],
  page: 1,
  limit: 100,
  totalPages: 1,
  totalResults: 0,
  startIndex: 1,
  endIndex: 100,
};

function reducer(state: any, action: any) {
  switch (action.type) {
    case "SET_DATA":
      return action.payload;
    default:
      return state;
  }
}

function Employees() {
  const [filterData, setFilterData] = useState({
    search: "",
  });
  const [loading, setLoading] = useState(true);
  const [data, dispatch] = useReducer(reducer, initialState);
  const location = useLocation();
  const navigate = useNavigate();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });

  const setData = (payload: any) => {
    const page = Number(payload?.page || 1);
    const limit = Number(payload?.limit || payload?.results?.length || 100);
    const results = (payload?.results || []).map((employee: any, index: number) => ({
      ...employee,
      serialNumber: (page - 1) * limit + index + 1,
    }));

    dispatch({
      type: "SET_DATA",
      payload: {
        ...payload,
        results,
      },
    });
  };

  useEffect(() => {
    const { page, search } = query;

    const queryParams: any = {
      page,
      search,
    };

    Object.keys(queryParams).forEach((key) => {
      if (!queryParams[key]) {
        delete queryParams[key];
      }
    });

    setLoading(true);

    getEmployees(queryParams)
      .then((response: any) => {
        setData(response.data);
      })
      .catch(() => {
        toast.error("Failed to fetch employees");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [location.search]);

  const columns = [
    {
      selector: (row: any) => `${row.serialNumber}.`,
      name: "S.No.",
      sortable: false,
      maxWidth: "70px",
      minWidth: "70px",
    },
    {
      selector: (row: EmployeeData) => row.name,
      name: "Employee Name",
      sortable: false,
      wrap: true,
    },
    {
      selector: (row: any) => (
        <Link
          className='me-1'
          to={`edit/${row.id}?${QueryString.stringify({
            redirect: location.pathname + location.search,
          })}`}
        >
          <Edit size={15} />
        </Link>
      ),
      name: "Actions",
      sortable: false,
      center: true,
      maxWidth: "90px",
      minWidth: "90px",
    },
  ];

  const handleFilter = () => {
    const queryParams: any = { ...query, page: 1, ...filterData };

    Object.keys(queryParams).forEach((key) => {
      if (!queryParams[key]) {
        delete queryParams[key];
      }
    });

    const queryString = QueryString.stringify(queryParams);
    navigate(`${location.pathname}${Object.keys(queryParams).length > 0 ? "?" + queryString : ""}`);
  };

  const handleClearFilter = () => {
    setFilterData({ search: "" });
    navigate(`${location.pathname}?${QueryString.stringify({ page: 1 })}`);
  };

  return (
    <div className='d-flex flex-column h-100 px-sm-1 w-100 pb-sm-1'>
      <Row className='align-items-center mb-2 page-title-row'>
        <Col sm>
          <h1 className='my-1 fw-bold h4'>Employee Master</h1>
        </Col>
        <Col sm='auto'>
          <div className='justify-content-between'>
            <Link
              to={`/employees/add?${QueryString.stringify({
                redirect: location.pathname + location.search,
              })}`}
              className='btn btn-sm btn-primary my-1 text-white'
            >
              Add New
            </Link>
          </div>
        </Col>
      </Row>

      <div className='flex-grow-1 overflow-hidden' style={{ height: "calc(100vh - 230px)" }}>
        <Card className='h-100 p-3 w-100'>
          <div className='d-flex flex-column h-100 rounded-0 list-data-grid-wrapper'>
            <DataGrid
              columns={columns}
              data={data}
              loading={loading}
              isFilterApplied={Object.keys(query).some((key) => key !== "page")}
              noDataTitle='No employees found'
              noDataSubtitle='There are no employees available right now.'
              noDataFilteredTitle='No employees match your filters'
              noDataFilteredSubtitle='Try changing or clearing the filter criteria.'
              FilterSubHeader={true}
              filterData={filterData}
              setFilterData={setFilterData}
              subHeaderComponent={
                <Row className='w-100 align-items-start g-3 py-1 px-1'>
                  <Col md={2}>
                    <FormInput
                      name='search'
                      label='Name'
                      placeholder='Search by name'
                      onChange={(e) =>
                        setFilterData({
                          ...filterData,
                          [e.target.name]: e.target.value,
                        })
                      }
                      value={filterData.search}
                    />
                  </Col>

                  <Col md='auto'>
                    <div className='filter-action-buttons'>
                      <Button color='primary' size='sm' onClick={() => handleFilter()}>
                        Filter
                      </Button>
                      <Button color='danger' size='sm' onClick={() => handleClearFilter()}>
                        Clear
                      </Button>
                    </div>
                  </Col>
                </Row>
              }
            />
          </div>
        </Card>
      </div>
    </div>
  );
}

export default Employees;
